import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  InputGroup,
  Form,
  Pagination,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetBlogsQuery,
  useToggleBlogStatusMutation,
  useToggleTopReadMutation,
  useDeleteBlogMutation,
} from "../../../redux/api/BlogsApi/blogsApi";
import { useGetBlogCategoriesQuery } from "../../../redux/api/BlogCategoriesApi/blogCategoriesApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaBlog,
  FaCheckCircle,
  FaTimesCircle,
  FaStar,
  FaTrash,
  FaEye,
} from "react-icons/fa";
import { message } from "antd";
import Swal from "sweetalert2";
import "./Blogs.css";

const BlogsListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const categoryId = searchParams.get("categoryId") || "";
  const sortBy = searchParams.get("sortBy") || "published_at";
  const order = searchParams.get("order") || "desc";
  const limit = Number(searchParams.get("limit")) || 10;

  const [searchTerm, setSearchTerm] = useState(search);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== search) {
        setSearchParams({
          page: 1,
          limit,
          search: searchTerm,
          categoryId,
          sortBy,
          order,
        });
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm, limit, sortBy, order, search, categoryId, setSearchParams]);

  const { data, isLoading, isFetching } = useGetBlogsQuery({
    page,
    limit,
    search,
    categoryId,
    sortBy,
    order,
  });

  const { data: categories } = useGetBlogCategoriesQuery();
  const [toggleStatus] = useToggleBlogStatusMutation();
  const [toggleTopRead] = useToggleTopReadMutation();
  const [deleteBlog] = useDeleteBlogMutation();

  const blogs = data?.blogs || [];
  const totalPages = Math.ceil((data?.total || 0) / limit);

  const handleToggleStatus = async (id) => {
    try {
      await toggleStatus(id).unwrap();
      message.success("Status updated");
    } catch (err) {
      message.error("Failed to update status");
    }
  };

  const handleToggleTopRead = async (id) => {
    try {
      await toggleTopRead(id).unwrap();
      message.success("Top Read status updated");
    } catch (err) {
      message.error("Failed to update Top Read status");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteBlog(id).unwrap();
        message.success("Blog deleted successfully");
      } catch (err) {
        message.error("Failed to delete blog");
      }
    }
  };

  if (isLoading)
    return (
      <div className="d-flex justify-content-center py-5">
        <LoaderSpinner />
      </div>
    );

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaBlog className="text-primary me-2" />
            Blog Management
          </h2>
          <p className="text-muted mb-0">
            Manage your blog posts and categories
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => navigate("/admin/blog-categories")}
            className="rounded-pill px-4 fw-semibold"
          >
            Manage Categories
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate("/admin/add-blog")}
            className="d-flex align-items-center gap-2 px-4 shadow-sm rounded-pill fw-semibold"
          >
            <FaPlus /> Add New Blog
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <Card.Header className="bg-white border-bottom p-3">
          <Row className="align-items-center g-2">
            <Col md={2}>
              <Form.Select
                size="sm"
                value={limit}
                onChange={(e) =>
                  setSearchParams({
                    page: 1,
                    limit: e.target.value,
                    search,
                    categoryId,
                    sortBy,
                    order,
                  })
                }
              >
                <option value={10}>Show 10</option>
                <option value={20}>Show 20</option>
                <option value={50}>Show 50</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={categoryId}
                onChange={(e) =>
                  setSearchParams({
                    page: 1,
                    limit,
                    search,
                    categoryId: e.target.value,
                    sortBy,
                    order,
                  })
                }
              >
                <option value="">All Categories</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={7}>
              <InputGroup className="bg-light rounded-pill border-0 overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search blogs..."
                  className="bg-transparent border-0 shadow-none py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4">Blog Post</th>
                <th>Category</th>
                <th className="text-center">Top Read</th>
                <th className="text-center">Status</th>
                <th className="text-center">Views</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((blog) => (
                <tr key={blog.id}>
                  <td className="ps-4 py-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3 position-relative">
                        <img
                          src={blog.image || "https://via.placeholder.com/50"}
                          alt=""
                          className="rounded-3"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                          }}
                          onError={(e) =>
                            (e.target.src = "https://via.placeholder.com/50")
                          }
                        />
                      </div>
                      <div>
                        <div className="fw-bold text-dark">{blog.title}</div>
                        <div className="text-muted extra-small">
                          {new Date(blog.published_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge
                      bg="info-light"
                      className="text-info fw-normal rounded-pill px-3"
                    >
                      {blog.category?.name || "Uncategorized"}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      className={`p-0 ${blog.top_read ? "text-warning" : "text-light"}`}
                      onClick={() => handleToggleTopRead(blog.id)}
                      title="Toggle Top Read"
                    >
                      <FaStar size={20} />
                    </Button>
                  </td>
                  <td className="text-center">
                    <Badge
                      bg={blog.status ? "success-light" : "danger-light"}
                      className={`cursor-pointer ${blog.status ? "text-success" : "text-danger"} rounded-pill px-3 py-2`}
                      onClick={() => handleToggleStatus(blog.id)}
                    >
                      {blog.status ? (
                        <FaCheckCircle className="me-1" />
                      ) : (
                        <FaTimesCircle className="me-1" />
                      )}
                      {blog.status ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <span className="small text-muted">
                      <FaEye className="me-1" /> {blog.view_count}
                    </span>
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="white"
                        size="sm"
                        className="btn-action shadow-sm border"
                        onClick={() => navigate(`/admin/edit-blog/${blog.id}`)}
                      >
                        <FaEdit className="text-primary" />
                      </Button>
                      <Button
                        variant="white"
                        size="sm"
                        className="btn-action shadow-sm border"
                        onClick={() => handleDelete(blog.id)}
                      >
                        <FaTrash className="text-danger" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {blogs.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    No blogs found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {totalPages > 1 && (
        <Pagination className="justify-content-center mt-4">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => setSearchParams({ ...searchParams, page: page - 1 })}
          />
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={page === i + 1}
              onClick={() => setSearchParams({ ...searchParams, page: i + 1 })}
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page === totalPages}
            onClick={() => setSearchParams({ ...searchParams, page: page + 1 })}
          />
        </Pagination>
      )}
    </div>
  );
};

export default BlogsListing;
